import { useState } from 'react';
import { Accordion, Group, Text, ActionIcon, Menu, Box, Button, rem, TextInput, Select, Stack, Paper, Tooltip } from '@mantine/core';
// TextInput is still used for phase editing
import { MoreVertical, Edit3, Archive, RotateCcw, Plus, ChevronUp, ChevronDown } from 'react-feather';
import type { PhaseWithSubGroups, TaskTemplateWithDetails, SubGroupWithTemplates, JobTitle, TaskAnchorType } from '../../../../types';
import { SubGroupSection } from './SubGroupSection';

interface PhaseAccordionProps {
  phases: PhaseWithSubGroups[];
  jobTitles?: JobTitle[];
  onAddPhase?: (name: string, position: 'start' | 'end' | string) => void;
  onEditPhase?: (phase: PhaseWithSubGroups) => void;
  onArchivePhase?: (phase: PhaseWithSubGroups) => void;
  onRestorePhase?: (phase: PhaseWithSubGroups) => void;
  onReorderPhase?: (phaseId: string, direction: 'up' | 'down') => void;
  onOpenAddSubGroupModal?: (phaseId: string, phaseName: string, subGroupCount: number) => void;
  onEditSubGroup?: (subGroup: SubGroupWithTemplates) => void;
  onArchiveSubGroup?: (subGroup: SubGroupWithTemplates) => void;
  onRestoreSubGroup?: (subGroup: SubGroupWithTemplates) => void;
  onAddTemplate: (subGroupId: string | null, phaseId?: string) => void;
  onCreateTaskInline?: (data: {
    phaseId: string;
    subGroupId: string | null;
    taskTitle: string;
    jobTitleId: string | null;
    anchorType: TaskAnchorType;
    daysOffset: number;
    isSessionSpecific: boolean;
  }) => void;
  onEditTemplate: (template: TaskTemplateWithDetails) => void;
  onDuplicateTemplate: (template: TaskTemplateWithDetails) => void;
  onArchiveTemplate: (template: TaskTemplateWithDetails) => void;
  onRestoreTemplate: (template: TaskTemplateWithDetails) => void;
  onDeleteTemplate: (template: TaskTemplateWithDetails) => void;
}

export function PhaseAccordion({
  phases,
  jobTitles = [],
  onAddPhase,
  onEditPhase,
  onArchivePhase,
  onRestorePhase,
  onReorderPhase,
  onOpenAddSubGroupModal,
  onEditSubGroup,
  onArchiveSubGroup,
  onRestoreSubGroup,
  onAddTemplate,
  onCreateTaskInline,
  onEditTemplate,
  onDuplicateTemplate,
  onArchiveTemplate,
  onRestoreTemplate,
  onDeleteTemplate,
}: PhaseAccordionProps) {
  // State for add phase inline form
  const [showAddPhaseForm, setShowAddPhaseForm] = useState(false);
  const [newPhaseName, setNewPhaseName] = useState('');
  const [newPhasePosition, setNewPhasePosition] = useState<string>('end');

  // State for editing phase inline
  const [editingPhaseId, setEditingPhaseId] = useState<string | null>(null);
  const [editingPhaseName, setEditingPhaseName] = useState('');

  // Count total templates in a phase
  const getPhaseTemplateCount = (phase: PhaseWithSubGroups): number => {
    return phase.sub_groups.reduce((sum, sg) => sum + sg.templates.length, 0);
  };

  // Build position options for the dropdown
  const positionOptions = [
    { value: 'end', label: 'At end' },
    { value: 'start', label: 'At beginning' },
    ...phases.map(p => ({ value: `after:${p.id}`, label: `After ${p.name}` })),
  ];

  const handleAddPhase = () => {
    if (newPhaseName.trim() && onAddPhase) {
      onAddPhase(newPhaseName.trim(), newPhasePosition);
      setNewPhaseName('');
      setNewPhasePosition('end');
      setShowAddPhaseForm(false);
    }
  };

  const handleCancelAddPhase = () => {
    setNewPhaseName('');
    setNewPhasePosition('end');
    setShowAddPhaseForm(false);
  };

  const handleStartEditPhase = (phase: PhaseWithSubGroups) => {
    setEditingPhaseId(phase.id);
    setEditingPhaseName(phase.name);
  };

  const handleSaveEditPhase = () => {
    if (editingPhaseName.trim() && editingPhaseId) {
      const phase = phases.find(p => p.id === editingPhaseId);
      if (phase && onEditPhase) {
        onEditPhase({ ...phase, name: editingPhaseName.trim() });
      }
      setEditingPhaseId(null);
      setEditingPhaseName('');
    }
  };

  const handleCancelEditPhase = () => {
    setEditingPhaseId(null);
    setEditingPhaseName('');
  };

  return (
    <Stack gap="md">
      {/* Add Phase Button / Inline Form */}
      {showAddPhaseForm ? (
        <Paper p="md" withBorder style={{ borderStyle: 'dashed' }}>
          <Text fw={600} size="sm" mb="md">Add New Phase</Text>
          <Group align="flex-end" gap="md">
            <TextInput
              label="Phase Name"
              placeholder="e.g., Pre-Program Planning"
              value={newPhaseName}
              onChange={(e) => setNewPhaseName(e.target.value)}
              required
              style={{ flex: 1 }}
            />
            <Select
              label="Position"
              data={positionOptions}
              value={newPhasePosition}
              onChange={(v) => setNewPhasePosition(v || 'end')}
              w={200}
            />
            <Group gap="xs">
              <Button variant="default" onClick={handleCancelAddPhase}>
                Cancel
              </Button>
              <Button onClick={handleAddPhase} disabled={!newPhaseName.trim()}>
                Add Phase
              </Button>
            </Group>
          </Group>
        </Paper>
      ) : (
        <Box
          p="md"
          style={{
            border: '1px dashed var(--mantine-color-gray-4)',
            borderRadius: 'var(--mantine-radius-md)',
            cursor: 'pointer',
          }}
          onClick={() => setShowAddPhaseForm(true)}
        >
          <Group gap="xs" justify="center">
            <Plus size={16} color="var(--mantine-color-gray-6)" />
            <Text size="sm" c="dimmed">Add Phase</Text>
          </Group>
        </Box>
      )}

      <Accordion
      variant="separated"
      radius="md"
      multiple
      chevronPosition="left"
      defaultValue={phases.map(p => p.id)}
    >
      {phases.map((phase, index) => {
        const templateCount = getPhaseTemplateCount(phase);
        const isFirst = index === 0;
        const isLast = index === phases.length - 1;

        return (
          <Accordion.Item
            key={phase.id}
            value={phase.id}
            style={{
              opacity: phase.is_archived ? 0.7 : 1,
            }}
          >
            {/* Phase header - normal or edit mode */}
            {editingPhaseId === phase.id ? (
              /* Inline edit mode */
              <Box p="md">
                <Group gap="md" align="center">
                  <TextInput
                    value={editingPhaseName}
                    onChange={(e) => setEditingPhaseName(e.target.value)}
                    style={{ flex: 1 }}
                    autoFocus
                  />
                  <Group gap="xs">
                    <Button variant="default" size="sm" onClick={handleCancelEditPhase}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSaveEditPhase} disabled={!editingPhaseName.trim()}>
                      Save Changes
                    </Button>
                  </Group>
                </Group>
              </Box>
            ) : (
              /* Normal view mode */
              <Box pos="relative">
                <Accordion.Control>
                  <Box pr={rem(100)}>
                    <Text fw={600} size="sm">
                      {phase.name}
                      {phase.is_archived && ' (Archived)'}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {templateCount} task{templateCount !== 1 ? 's' : ''}
                    </Text>
                  </Box>
                </Accordion.Control>

                {/* Action buttons positioned outside the control to avoid nested buttons */}
                <Group
                  gap="xs"
                  wrap="nowrap"
                  pos="absolute"
                  top="50%"
                  right={rem(16)}
                  style={{ transform: 'translateY(-50%)' }}
                >
                  {/* Reorder buttons */}
                  <Tooltip label="Move phase up or down in order" position="top">
                    <Group gap={4} wrap="nowrap">
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        disabled={isFirst}
                        onClick={(e) => {
                          e.stopPropagation();
                          onReorderPhase?.(phase.id, 'up');
                        }}
                      >
                        <ChevronUp size={14} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        disabled={isLast}
                        onClick={(e) => {
                          e.stopPropagation();
                          onReorderPhase?.(phase.id, 'down');
                        }}
                      >
                        <ChevronDown size={14} />
                      </ActionIcon>
                    </Group>
                  </Tooltip>

                  {/* Phase menu */}
                  <Menu position="bottom-end" withinPortal>
                    <Menu.Target>
                      <ActionIcon variant="subtle" size="sm">
                        <MoreVertical size={14} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<Edit3 size={14} />}
                        onClick={() => handleStartEditPhase(phase)}
                      >
                        Edit Phase
                      </Menu.Item>
                    {onOpenAddSubGroupModal && (
                      <Menu.Item
                        leftSection={<Plus size={14} />}
                        onClick={() => onOpenAddSubGroupModal(phase.id, phase.name, phase.sub_groups.length)}
                      >
                        Add Sub-Group
                      </Menu.Item>
                    )}
                    <Menu.Divider />
                    {phase.is_archived ? (
                      onRestorePhase && (
                        <Menu.Item
                          leftSection={<RotateCcw size={14} />}
                          onClick={() => onRestorePhase(phase)}
                        >
                          Restore Phase
                        </Menu.Item>
                      )
                    ) : (
                      onArchivePhase && (
                        <Menu.Item
                          leftSection={<Archive size={14} />}
                          onClick={() => onArchivePhase(phase)}
                        >
                          Archive Phase
                        </Menu.Item>
                      )
                    )}
                  </Menu.Dropdown>
                </Menu>
              </Group>
              </Box>
            )}

            <Accordion.Panel>
              <Box py="sm">
                {phase.sub_groups.length === 0 ? (
                  <Box ta="center" py="xl">
                    <Text size="sm" c="dimmed" mb="md">
                      No sub-groups in this phase yet
                    </Text>
                    {onOpenAddSubGroupModal && (
                      <Button
                        variant="light"
                        size="xs"
                        leftSection={<Plus size={14} />}
                        onClick={() => onOpenAddSubGroupModal(phase.id, phase.name, 0)}
                      >
                        Add Sub-Group
                      </Button>
                    )}
                  </Box>
                ) : (
                  <>
                    {phase.sub_groups.map(subGroup => (
                      <SubGroupSection
                        key={subGroup.id}
                        subGroup={subGroup}
                        phaseId={phase.id}
                        jobTitles={jobTitles}
                        onEditSubGroup={onEditSubGroup}
                        onArchiveSubGroup={onArchiveSubGroup}
                        onRestoreSubGroup={onRestoreSubGroup}
                        onAddTemplate={(subGroupId) => onAddTemplate(subGroupId, phase.id)}
                        onCreateTaskInline={onCreateTaskInline}
                        onEditTemplate={onEditTemplate}
                        onDuplicateTemplate={onDuplicateTemplate}
                        onArchiveTemplate={onArchiveTemplate}
                        onRestoreTemplate={onRestoreTemplate}
                        onDeleteTemplate={onDeleteTemplate}
                      />
                    ))}

                    {/* Add sub-group button */}
                    {onOpenAddSubGroupModal && (
                      <Button
                        variant="subtle"
                        size="xs"
                        leftSection={<Plus size={14} />}
                        mt="sm"
                        onClick={() => onOpenAddSubGroupModal(phase.id, phase.name, phase.sub_groups.length)}
                      >
                        Add sub-group
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Accordion.Panel>
          </Accordion.Item>
        );
      })}
    </Accordion>
    </Stack>
  );
}

export default PhaseAccordion;
